import { ReactElement, useEffect, useMemo, useState } from 'react';
import Link from 'next/link';
import { NextRouter, useRouter } from 'next/router';
import { Divider, message, Row, Tooltip } from 'antd';
import { Auth } from 'aws-amplify';
import { format, isValid, parse } from 'date-fns';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';

import { Checkmark, Close, GoogleLogo } from '@assets/icons';
import { CognitoHostedUIIdentityProvider } from '@aws-amplify/auth';
import { Button } from '@components/Button';
import { Form } from '@components/Form';
import { Input, PasswordInput } from '@components/Input';
import { UsersCommandApi } from '@lib/api/endpoints/users';
import { setTempUserInfo } from '@lib/redux.lib/slices/appSlice';
import { RootState } from '@lib/redux.lib/store';
import { logIfDevelopment } from '@lib/utils/logging';
import { displayError, validatePassword } from '@lib/utils/methods';
import { checkEmailValid } from '@lib/utils/stringHelper';
import { buildTermsPrivacyConsentMetadata } from '@lib/analytics/termsPrivacyConsentMetadata';
import { useClientEnvironment } from '@hooks/clientEnvironment';

import { useSignupPageContext } from '../SignupPageContext';
import { StyledGapContainer } from '../styles';

import { AccountCreationHeader } from './Header/AccountCreationHeader';
import { LegalChecks } from './LegalChecks/LegalChecks';
import { buildApplicationIdFromQuery } from './helper';

export const AccountCreation = (): ReactElement => {
  const { isUkSelected, selectedCountry, birthDateString, tempUserId } =
    useSignupPageContext();

  const { data: clientEnvironment } = useClientEnvironment();
  const {
    privacy_policy_domain_uk,
    privacy_policy_domain_us,
    terms_of_service_domain_uk,
    terms_of_service_domain_us,
  } = clientEnvironment || {};

  const consentTermsUrl = isUkSelected
    ? terms_of_service_domain_uk
    : terms_of_service_domain_us;
  const consentPrivacyUrl = isUkSelected
    ? privacy_policy_domain_uk
    : privacy_policy_domain_us;

  const birthday = useMemo(
    () => (isUkSelected ? parseUkBirthDateString(birthDateString) : null),
    [isUkSelected, birthDateString]
  );

  const [form] = Form.useForm<IFieldValues>();
  const [fieldValues, setFieldValues] = useState<IFieldValues>({});
  const [commonPasswords, setCommonPasswords] =
    useState<{ [password: string]: boolean }>(null);
  const [passwordErrorMsg, setPasswordErrorMsg] = useState('');
  const [signUpLoading, setSignUpLoading] = useState(false);
  const router = useRouter();
  const redirectTo = router.query?.redirectTo;
  const userNeedsNewPassword = !!router.query?.newPasswordRequired;
  const tempUserInfo = useSelector(
    (state: RootState) => state.app.user.tempUser
  );
  const dispatch = useDispatch();

  useEffect(() => {
    const validateUserWithTempPwdInfo = () => {
      if (userNeedsNewPassword) {
        if (!tempUserInfo) {
          router.push('/auth/login');
          return;
        }

        const email = tempUserInfo?.challengeParam?.userAttributes?.['email'];

        if (!email) {
          router.push('/auth/login');
          return;
        }

        setFieldValues({ email });
      }
    };

    validateUserWithTempPwdInfo();
  }, [tempUserInfo, userNeedsNewPassword, router]);

  // On Component Mount - get list of common passwords
  useEffect(() => {
    fetch(
      'https://app-constants.s3.us-west-2.amazonaws.com/badpassword.json'
    ).then((res) => res.json().then((result) => setCommonPasswords(result)));
  }, []);

  const emailLowerCase = fieldValues?.email?.toLowerCase()?.trim();

  // TODO: optimize needed -- but necessary on each render for last two password checks
  const isPasswordBad = fieldValues?.password
    ? validatePassword(
        fieldValues.password,
        [{ label: 'Email', value: emailLowerCase }],
        commonPasswords
      )
    : true;

  const passwordCheckResults = passwordChecks(
    fieldValues?.password,
    isPasswordBad
  );

  const getIsPasswordValid = (password: string) => {
    const isPasswordBad = password
      ? validatePassword(
          password,
          [{ label: 'Email', value: emailLowerCase }],
          commonPasswords
        )
      : true;

    const passwordCheckResults = passwordChecks(password, isPasswordBad);

    return passwordStrength(passwordCheckResults);
  };

  const setPasswordErrMsg = (skipValidation = false) => {
    if (skipValidation) return setPasswordErrorMsg('');

    const isPasswordBad = fieldValues.password
      ? validatePassword(
          fieldValues.password,
          [{ label: 'Email', value: emailLowerCase }],
          commonPasswords
        )
      : true;
    const passwordCheckResults = passwordChecks(
      fieldValues.password,
      isPasswordBad
    );
    const isPasswordValid = passwordStrength(passwordCheckResults);
    if (!isPasswordValid || !fieldValues.password) {
      setPasswordErrorMsg(
        !fieldValues.password
          ? 'Password is required!'
          : 'Password does not meet all the requirements'
      );
    } else setPasswordErrorMsg('');
  };

  const checkList: Array<keyof IFieldValues> = isUkSelected
    ? ['eligibility', 'age', 'insurance-number', 'terms']
    : ['eligibility', 'terms'];
  const isAllLegalChecked = checkList.every((check) => !!fieldValues[check]);

  const isSignupButtonDisabled =
    !getIsPasswordValid(fieldValues.password) ||
    signUpLoading ||
    !isAllLegalChecked ||
    !getIsEmailValid(fieldValues.email);

  return (
    <StyledContainer>
      <AccountCreationHeader />

      <div>
        <StyledForm
          form={form}
          name='signup'
          layout={'vertical'}
          onValuesChange={(changedValues: IFieldValues) => {
            // changedValues - only update changed value
            // values - update all fields
            setFieldValues({ ...fieldValues, ...changedValues });
          }}
        >
          {/* form items must produce a single child element */}
          {!userNeedsNewPassword && <EmailFormItem />}

          <StyledGapContainer gap={8}>
            <div>
              <PasswordFormItem
                getIsPasswordValid={getIsPasswordValid}
                validateStatus={passwordErrorMsg ? 'error' : 'validating'}
                setPasswordErrMsg={setPasswordErrMsg}
              />
              <ErrorMsg passwordErrorMsg={passwordErrorMsg}>
                {passwordErrorMsg}
              </ErrorMsg>
            </div>

            <PasswordCheckItems passwordCheckResults={passwordCheckResults} />
          </StyledGapContainer>

          <StyledGapContainer gap={24}>
            <LegalChecks />
            <SignUpButton
              type='primary'
              htmlType='submit'
              disabled={isSignupButtonDisabled}
              onClick={() =>
                signUp({
                  fieldValues,
                  isPasswordBad,
                  setSignUpLoading: (loading: boolean) =>
                    setSignUpLoading(loading),
                  router,
                  redirectTo: redirectTo as string,
                  selectedCountry,
                  birthday,
                  userNeedsNewPassword,
                  tempUserInfo,
                  dispatch,
                  tempUserId,
                  consentTermsUrl,
                  consentPrivacyUrl,
                })
              }
            >
              Sign up
            </SignUpButton>
          </StyledGapContainer>
        </StyledForm>
        <div>
          <StyledDivider>or</StyledDivider>
          <GoogleButton selectedCountry={selectedCountry} birthday={birthday} />
          <LoginLink />
        </div>
      </div>
    </StyledContainer>
  );
};

const StyledContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  max-width: 448px;
  margin: 0 auto;
  padding: 64px 20px 0 20px;
`;

const ErrorMsg = styled.p<{ passwordErrorMsg: string }>`
  display: ${({ passwordErrorMsg }) =>
    passwordErrorMsg ? 'inline-block' : 'none'};
  color: ${(props) => props.theme.palette.color[0]};
  margin: 4px 0 0 0;
  font-size: 12px;
`;

interface IFieldValues {
  email?: string;
  password?: string;
  secret_key?: string;
  eligibility?: boolean;
  terms?: boolean;
  age?: boolean;
  'insurance-number'?: boolean;
}

interface IPasswordCheck {
  rule: number | boolean;
  text: string;
}

const passwordChecks = (
  password: string,
  isPasswordBad: boolean | { label: string; value: string }
): IPasswordCheck[] => [
  { rule: password?.length >= 8, text: '8 characters minimum' },
  {
    rule: password?.match(/[A-Z]+/)?.length,
    text: 'One uppercase letter',
  },
  {
    rule: password?.match(/[a-z]+/)?.length,
    text: 'One lowercase letter',
  },
  {
    rule: password?.match(/[0-9]+/)?.length,
    text: 'One number',
  },
  {
    rule: !isPasswordBad,
    text: 'Does not include parts of name or email',
  },
  {
    rule: !isPasswordBad,
    text: 'Not a commonly used password (e.g. Password123)',
  },
];

// // for UI display only
const PasswordCheckItems = ({
  passwordCheckResults,
}: {
  passwordCheckResults: IPasswordCheck[];
}): ReactElement => {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {passwordCheckResults?.map((val, index) => (
        <PasswordLine key={index}>
          {val?.rule ? <Checkmark /> : <Close />}
          <span style={{ paddingLeft: 8 }}>{val?.text}</span>
        </PasswordLine>
      ))}{' '}
    </div>
  );
};

// for actual validation
const passwordStrength = (passwordCheckResults: IPasswordCheck[]) => {
  return !passwordCheckResults?.filter((val) => !val?.rule)?.length;
};

type SignUpProps = {
  fieldValues: IFieldValues;
  isPasswordBad: boolean | { label: string; value: string };
  setSignUpLoading: (loading: boolean) => void;
  router: NextRouter;
  redirectTo?: string | undefined;
  selectedCountry: SelectedCountryType;
  birthday: string | null;
  userNeedsNewPassword?: boolean;
  tempUserInfo?: any;
  dispatch: any;
  tempUserId: number | null;
  consentTermsUrl?: string;
  consentPrivacyUrl?: string;
};
const signUp = async ({
  fieldValues,
  isPasswordBad,
  setSignUpLoading,
  router,
  redirectTo,
  selectedCountry,
  birthday,
  userNeedsNewPassword,
  tempUserInfo,
  tempUserId,
  dispatch,
  consentTermsUrl,
  consentPrivacyUrl,
}: SignUpProps): Promise<void> => {
  // Set signUpLoading to true to indicate account is being created, and to lock multiple clicks;
  setSignUpLoading(true);

  const { email, password } = fieldValues;

  try {
    // failsafe if somehow password is still bad on submit
    if (isPasswordBad && typeof isPasswordBad === 'object') {
      throw Error(
        `Please use a stronger password. Passwords cannot contain any pieces of ${
          isPasswordBad?.label
            ? isPasswordBad.label
            : 'commonly used passwords.'
        } `
      );
    }

    const username = email?.toLowerCase()?.trim();

    if (userNeedsNewPassword) {
      await setNewPassword(
        tempUserInfo,
        password,
        birthday,
        selectedCountry,
        router,
        tempUserId
      );
      setSignUpLoading(false);
      dispatch(setTempUserInfo(null));
      router.push('/');
      return;
    } else {
      // Signup on Cognito
      const applicationId = buildApplicationIdFromQuery(router?.query);
      const consentMetadata = await buildTermsPrivacyConsentMetadata(
        'signup',
        { termsUrl: consentTermsUrl, privacyUrl: consentPrivacyUrl }
      );

      // Persist the URLs the user actually accepted so the confirm page
      // (separate route, no access to the signup country selection) can
      // send the same URLs to PostConfirmation_ConfirmSignUp. localStorage
      // is used (not sessionStorage) because the email verification link
      // opens in a new tab where sessionStorage is not available. The entry
      // is keyed by email so different users on the same browser don't
      // collide, and is removed by the confirm page after it is read.
      if (typeof window !== 'undefined' && username) {
        localStorage.setItem(
          `terms_privacy_consent_urls:${username}`,
          JSON.stringify({
            termsUrl: consentTermsUrl,
            privacyUrl: consentPrivacyUrl,
          })
        );
      }

      await Auth.signUp({
        username,
        password,
        clientMetadata: {
          ...(applicationId ? { application_id: applicationId } : {}),
          selectedCountry,
          referrer: window?.location.origin,
          birthday,
          ...consentMetadata,
        },
      });
    }

    setSignUpLoading(false);

    // Change to post-signup instructions on success
    if (router.pathname === '/auth/signup') {
      router.push({
        pathname: '/auth/post-signup',
        query: {
          email: username,
          ...(redirectTo && { redirectTo }),
        },
      });
    }
  } catch (error) {
    console.error(error); // intentional
    if (
      error?.message.toString() ==
      'PreSignUp failed with error Incorrect Access Code.'
    ) {
      message.error('Incorrect Access Code.', 3);
      return setSignUpLoading(false);
    } else {
      displayError(error?.message);
      return setSignUpLoading(false);
    }
  }
};

type SetNewPasswordFn = (
  tempUserInfo: any,
  password: string,
  birthday: string,
  selectedCountry: ProductionCountry,
  router: NextRouter,
  tempUserId: number | null
) => Promise<void>;

const setNewPassword: SetNewPasswordFn = async (
  tempUserInfo,
  password,
  birthday,
  selectedCountry,
  router,
  tempUserId
) => {
  if (!tempUserId) {
    logIfDevelopment('user id not found for cognito user');
    throw new Error('Something went wrong');
  }
  await Auth.completeNewPassword(
    tempUserInfo,
    password,
    {},
    {
      referrer: window?.location.origin,
      application_id: router?.query?.application_id as string,
      ...(selectedCountry && { selected_country: selectedCountry }),
      ...(birthday ? { birthday } : {}),
    }
  );
  // we have an auth token at this point
  // TODO: remove all temp passwords for the given user, call an endpoint

  if (birthday) {
    await UsersCommandApi.createUserAppearance(tempUserId, { birthday });
  }
  if (selectedCountry) {
    const signupProperties = {
      selected_country: selectedCountry,
      referrer: window?.location.origin,
      client_id: process.env.NEXT_PUBLIC_CLIENT_ID,
    };
    await UsersCommandApi.create_user_signup_properties(
      tempUserId,
      signupProperties
    );
  }

  message.success('Signup successful! New password has been registered');
};

const getIsEmailValid = (email: string) => !!(email && checkEmailValid(email));

const EmailFormItem = (): ReactElement => {
  return (
    <StyledFormItem
      name={'email'}
      rules={[
        {
          message: 'Email is required!',
          required: true,
        },
        {
          message: 'Please enter a valid email address!',
          type: 'email',
          validator: (_, email) => {
            if (!email) return Promise.resolve(); // this is already handled in the above rule
            const isValidEmail = checkEmailValid(email);
            if (!isValidEmail) return Promise.reject();
            return Promise.resolve();
          },
        },
      ]}
    >
      <StyledInput
        name='email'
        label='Email'
        requiredSymbol
        displayBr={false}
        labelStyle={{
          fontWeight: 600,
          fontSize: 12,
          lineHeight: '16px',
          display: 'flex',
          gap: '4px',
        }}
        placeholder='name@example.com'
      />
    </StyledFormItem>
  );
};
const PasswordFormItem = ({
  getIsPasswordValid,
  validateStatus,
  setPasswordErrMsg,
}: {
  getIsPasswordValid: (password: string) => boolean;
  validateStatus: 'validating' | 'error';
  setPasswordErrMsg: (skipValidation?: boolean) => void;
}): ReactElement => (
  <StyledFormItem name={'password'} validateStatus={validateStatus}>
    <StyledPasswordInput
      name='password'
      label='Password'
      labelStyle={{
        fontWeight: 600,
        fontSize: 12,
        lineHeight: '16px',
        display: 'flex',
        gap: '4px',
      }}
      placeholder='*****'
      requiredSymbol
      onBlur={() => setPasswordErrMsg()}
      displayBr={false}
      onChange={(e) =>
        getIsPasswordValid(e.target.value) && setPasswordErrMsg(true)
      }
    />
  </StyledFormItem>
);

const StyledFormItem = styled(Form.Item)`
  &.ant-form-item {
    margin-bottom: 0;
  }

  .ant-form-item-control {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .ant-form-item-explain-error {
    margin: 0;
  }
`;
const StyledInput = styled(Input)`
  &.ant-input {
    height: 32px;
    margin-top: 4px;
  }
`;

const StyledPasswordInput = styled(PasswordInput)`
  &.ant-input-password {
    height: 32px;
    margin: 4px 0 0;

    .ant-input {
      font-size: 12px;
    }
  }
`;

type GoogleButtonProps = {
  selectedCountry: SelectedCountryType;
  birthday: string | null;
};
const GoogleButton = ({
  selectedCountry,
  birthday,
}: GoogleButtonProps): ReactElement => {
  const isFacebookBrowser =
    navigator?.userAgent?.includes('FB_IAB') ||
    navigator?.userAgent?.includes('FBIOS') ||
    navigator?.userAgent?.includes('FBAV') ||
    navigator?.userAgent?.includes('FBAN');

  return (
    <Tooltip
      title={
        isFacebookBrowser
          ? 'Google Sign In is not supported in Facebook Browser. Please open the application link with your native browser, such as Chrome or Safari, or on a laptop/computer.'
          : null
      }
    >
      <StyledGoogleButton
        type='default'
        disabled={isFacebookBrowser}
        onClick={() => {
          Auth.federatedSignIn({
            provider: CognitoHostedUIIdentityProvider.Google,
            customState: JSON.stringify({
              signupProperties: { selectedCountry },
              birthday,
            }),
          })
            .then((success) => {
              console.debug('sucess', success);
            })
            .catch((err) => {
              console.debug('ERROR', err);
            });
        }}
      >
        <GoogleLogo />
        <span style={{ paddingLeft: 8 }}>Sign up with Google</span>
      </StyledGoogleButton>
    </Tooltip>
  );
};

const LoginLink = (): ReactElement => (
  <StyledLoginLink>
    <span>Already have an account?</span>{' '}
    <Link href='/auth/login'>Log in here</Link>
  </StyledLoginLink>
);

const StyledLoginLink = styled.p`
  margin: 0;
  font-size: 12px;
  line-height: 15px;
  text-align: center;

  > span {
    color: ${({ theme }) => theme.palette.greyscale[7]};
  }

  > a {
    text-decoration: none;
    color: ${(props) => props.theme?.palette?.primary?.[0]};
    font-weight: 600;
  }
`;

const StyledForm = styled(Form)`
  &.ant-form {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }
`;

const PasswordLine = styled(Row)`
  align-items: center;
  font-size: 12px;
`;

const StyledDivider = styled(Divider)`
  &.ant-divider {
    margin: 12px 0;
  }

  &::before {
    border-block-start-color: ${(props) =>
      props.theme?.palette?.greyscale?.[4]} !important;
  }
  span.ant-divider-inner-text {
    font-size: 12px;
    padding: 0px 10px;
    color: ${(props) =>
      props?.theme ? props.theme?.palette?.greyscale?.[5] : 'inherit'};
  }
  &::after {
    border-block-start-color: ${(props) =>
      props.theme?.palette?.greyscale?.[4]} !important;
  }
`;

const SignUpButton = styled(Button)`
  &.ant-btn {
    width: 100%;
    align-items: center;
    justify-content: center;
    margin: 0;
    height: 32px;
    font-size: 12px;
  }
`;

const StyledGoogleButton = styled(Button)`
  &.ant-btn.ant-btn-default {
    border: 1px solid ${({ theme }) => theme.palette.stroke[0]};
    width: 100%;
    height: 32px;
    margin: 0 0 20px 0;
  }

  span {
    font-size: 12px;
    color: ${({ theme }) => theme.palette.greyscale[7]};
  }
`;

AccountCreation.isPublic = true;

const parseUkBirthDateString = (birthdateString: string | undefined) => {
  if (!birthdateString) return null;

  const parsedDate = parse(birthdateString, 'dd/MM/yyyy', new Date());
  const isValidDate =
    isValid(parsedDate) && format(parsedDate, 'dd/MM/yyyy') === birthdateString;

  if (!isValidDate) return null;

  return format(parsedDate, 'MM/dd/yyyy');
};
